import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Chip,
  Grid2,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import { getWorkspaces, openWorkspace } from "../../actions/workspaceAction";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import AddButton from "../../themes/overrides/AddButton";
import SearchField from "../../themes/overrides/SearchField";
import WorkspaceModal from "./WorkspaceModal";
import LoadingOverlay from "../../components/LoadingOverlay";
import WorkspaceIcon from "../../components/icons/WorkspaceIcon";
import ContentIcon from "../../components/icons/ContentIcon";
import KeyIcon from "../../components/icons/KeyIcon";
import UsersIcon from "../../components/icons/UsersIcon";
import GridIcon from "../../components/icons/GridIcon";
import ListIcon from "../../components/icons/ListIcon";

/**
 * Workspace picker.
 *
 * Workspaces are destinations, not rows of homogeneous data — there are a
 * handful of them and the job is to recognise the right one and go. So the
 * default view is a card per workspace carrying what actually distinguishes
 * them (how much is in it, who else is in it, what standing you have in it),
 * with the dense table kept behind a toggle for people who prefer it.
 */

const VIEW_STORAGE_KEY = "workspaceListView";

// A stable accent per workspace, derived from its id. Workspaces are things
// people return to daily, so a colour that never moves is a real wayfinding
// aid — it is the one piece of colour on an otherwise quiet card.
const ACCENTS = [
  { fg: "#4f46e5", bg: "rgba(99, 102, 241, 0.12)" },
  { fg: "#0f766e", bg: "rgba(13, 148, 136, 0.12)" },
  { fg: "#b45309", bg: "rgba(245, 158, 11, 0.14)" },
  { fg: "#be185d", bg: "rgba(219, 39, 119, 0.12)" },
  { fg: "#5b21b6", bg: "rgba(124, 58, 237, 0.12)" },
  { fg: "#1d4ed8", bg: "rgba(37, 99, 235, 0.12)" },
];

const accentFor = (id = "") => {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return ACCENTS[hash % ACCENTS.length];
};

const monogramFor = (name = "") => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

const ROLE_STYLES = {
  admin: { color: "#4f46e5", backgroundColor: "rgba(99, 102, 241, 0.12)" },
  editor: { color: "#0f766e", backgroundColor: "rgba(13, 148, 136, 0.12)" },
  viewer: { color: "#475569", backgroundColor: "rgba(100, 116, 139, 0.12)" },
};

/**
 * `createdAt` is written with `toLocaleString()` on the server rather than as
 * an ISO string or a Date, so it is only sometimes parseable here. Show a date
 * when one can be recovered and nothing at all when it cannot, rather than
 * printing "Invalid Date".
 */
const formatCreatedAt = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * `defaultAiModel` is `{ text, image }` on current workspaces and a bare model
 * id on older ones (see ai-provider.service.ts). Either way it is an id, not a
 * label, so resolve it against the workspace's own `aiModels` and show the
 * provider the way the rest of the app does. Anything unresolvable shows
 * nothing rather than a raw id.
 */
const resolveAiModel = (workspace) => {
  const raw = workspace?.defaultAiModel;
  const textId = typeof raw === "string" ? raw : raw?.text;
  if (!textId) return null;
  const models = Array.isArray(workspace?.aiModels) ? workspace.aiModels : [];
  const match = models.find((model) => String(model?.id) === String(textId));
  return match?.model ? String(match.model).toUpperCase() : null;
};

const Stat = ({ icon, value, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
    <Box sx={{ color: "#94a3b8", display: "flex", flexShrink: 0 }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#0f172a", lineHeight: 1.2 }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: 11, color: "#94a3b8", lineHeight: 1.3 }}>{label}</Typography>
    </Box>
  </Box>
);

const WorkspaceList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [shouldRefresh, setShouldRefresh] = useState(true);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [modalOpen, setModalOpen] = useState(false);
  // Ids whose logo image failed to load, so the card can fall back to the
  // monogram instead of showing an empty tile.
  const [brokenLogos, setBrokenLogos] = useState(() => new Set());
  const [view, setView] = useState(() => {
    try {
      return localStorage.getItem(VIEW_STORAGE_KEY) === "list" ? "list" : "grid";
    } catch {
      return "grid";
    }
  });

  const workspaces = useSelector((state) => state.workspace.workspaces);

  const currentUserId = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userInfo"))?._id ?? null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (shouldRefresh) {
      dispatch(getWorkspaces());
      setShouldRefresh(false);
    }
  }, [shouldRefresh, dispatch]);

  const selectView = (next) => {
    setView(next);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      /* a remembered view is a convenience; ignore blocked storage */
    }
  };

  const open = async (workspaceId) => {
    if (loading) return;
    try {
      setLoading(true);
      await dispatch(openWorkspace(workspaceId, navigate));
    } catch (error) {
      console.log("Error loading workspace");
    } finally {
      setLoading(false);
    }
  };

  // Normalise once: both views and the summary read from the same shape.
  const items = useMemo(() => {
    if (!Array.isArray(workspaces)) return [];
    return workspaces.map((workspace) => {
      const owner = Array.isArray(workspace?.createdBy) ? workspace.createdBy[0] : null;
      const members = Array.isArray(workspace?.users) ? workspace.users : [];
      const myMembership = currentUserId
        ? members.find((member) => String(member?.user) === String(currentUserId))
        : null;
      const locales = (Array.isArray(workspace?.locales) ? workspace.locales : []).filter(
        (locale) => typeof locale === "string",
      );
      const readKeys = Array.isArray(workspace?.apiKeys) ? workspace.apiKeys.length : 0;
      const writeKeys = Array.isArray(workspace?.writeApiKeys)
        ? workspace.writeApiKeys.length
        : 0;

      return {
        id: workspace._id,
        _id: workspace._id,
        name: typeof workspace?.name === "string" && workspace.name ? workspace.name : "Untitled",
        ownerName: typeof owner?.name === "string" && owner.name ? owner.name : "Unknown",
        ownerAvatar: owner?.avatarUrl || null,
        isOwnedByMe: currentUserId ? String(owner?._id) === String(currentUserId) : false,
        role: typeof myMembership?.role === "string" ? myMembership.role : null,
        memberCount: members.length,
        locales,
        readKeys,
        writeKeys,
        apiKeys: readKeys + writeKeys,
        aiModel: resolveAiModel(workspace),
        logoUrl: typeof workspace?.logoUrl === "string" && workspace.logoUrl ? workspace.logoUrl : null,
        // Production-only counter, and undefined on workspaces created before
        // it was introduced — distinguish "none yet" from "not known".
        entries: typeof workspace?.totalEntries === "number" ? workspace.totalEntries : null,
        createdAt: formatCreatedAt(workspace?.createdAt),
        createdAtRaw: workspace?.createdAt || null,
      };
    });
  }, [workspaces, currentUserId]);

  const visible = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const filtered = term
      ? items.filter(
          (item) =>
            item.name.toLowerCase().includes(term) ||
            item.ownerName.toLowerCase().includes(term),
        )
      : items;

    const sorted = [...filtered];
    if (sortBy === "entries") {
      sorted.sort((a, b) => (b.entries ?? -1) - (a.entries ?? -1));
    } else if (sortBy === "members") {
      sorted.sort((a, b) => b.memberCount - a.memberCount);
    } else {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
    }
    return sorted;
  }, [items, searchTerm, sortBy]);

  const summary = useMemo(() => {
    const known = items.filter((item) => item.entries !== null);
    return {
      total: items.length,
      owned: items.filter((item) => item.isOwnedByMe).length,
      entries: known.reduce((sum, item) => sum + item.entries, 0),
      hasEntryCounts: known.length > 0,
    };
  }, [items]);

  const columns = [
    { field: "name", headerName: "Workspace", flex: 1, minWidth: 180 },
    { field: "ownerName", headerName: "Owner", flex: isMobile ? 0.5 : 0.35, minWidth: 130 },
    {
      field: "role",
      headerName: "Your role",
      flex: 0.3,
      minWidth: 110,
      renderCell: (params) =>
        params.value ? (
          <Chip
            label={params.value}
            size="small"
            sx={{
              height: 22,
              fontSize: 11,
              fontWeight: 600,
              textTransform: "capitalize",
              ...(ROLE_STYLES[params.value] || ROLE_STYLES.viewer),
            }}
          />
        ) : (
          "—"
        ),
    },
    {
      field: "entries",
      headerName: "Entries",
      flex: 0.25,
      minWidth: 100,
      renderCell: (params) => (params.value === null ? "—" : params.value.toLocaleString()),
    },
    { field: "memberCount", headerName: "Members", flex: 0.25, minWidth: 100 },
    {
      field: "locales",
      headerName: "Locales",
      flex: 0.3,
      minWidth: 110,
      renderCell: (params) => (params.value?.length ? params.value.join(", ") : "—"),
    },
    {
      field: "createdAt",
      headerName: "Created",
      flex: 0.3,
      minWidth: 120,
      renderCell: (params) => params.value || "—",
    },
  ];

  const hasWorkspaces = items.length > 0;
  const hasResults = visible.length > 0;

  return (
    <Box>
      {/* Page header */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, minWidth: 0 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: "12px",
              background: "rgba(99, 102, 241, 0.1)",
              color: "#4f46e5",
              display: "flex",
            }}
          >
            <WorkspaceIcon size={isMobile ? 28 : 32} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h1" sx={{ mb: 0.5 }}>
              Workspaces
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {hasWorkspaces
                ? `${summary.total} workspace${summary.total === 1 ? "" : "s"}` +
                  `${summary.owned ? ` · ${summary.owned} you own` : ""}` +
                  `${summary.hasEntryCounts ? ` · ${summary.entries.toLocaleString()} entries in production` : ""}`
                : "Manage and organize your project workspaces"}
            </Typography>
          </Box>
        </Box>
        <AddButton
          label="Create workspace"
          sx={{ boxShadow: "0 10px 15px -3px rgba(99, 102, 241, 0.3)" }}
          onClick={() => setModalOpen(true)}
        />
      </Box>

      {/* Controls */}
      <Box
        className="glass-card"
        sx={{
          p: 2,
          borderRadius: "16px",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 2,
          mb: 3,
        }}
      >
        <SearchField
          placeholder="Search by name or owner..."
          variant="outlined"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          sx={{ flex: "1 1 260px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />

        <Select
          value={sortBy}
          onChange={(event) => setSortBy(event.target.value)}
          size="small"
          sx={{
            minWidth: 170,
            height: 44,
            borderRadius: "12px",
            backgroundColor: "rgba(255,255,255,0.8)",
            "& fieldset": { borderColor: "rgba(226, 232, 240, 0.8)" },
          }}
        >
          <MenuItem value="name">Sort by name</MenuItem>
          <MenuItem value="entries">Sort by entries</MenuItem>
          <MenuItem value="members">Sort by members</MenuItem>
        </Select>

        <Box sx={{ display: "flex", gap: 0.5, ml: { xs: 0, md: "auto" } }}>
          {[
            { key: "grid", label: "Card view", icon: GridIcon },
            { key: "list", label: "Table view", icon: ListIcon },
          ].map(({ key, label, icon: Icon }) => (
            <Tooltip key={key} title={label} arrow>
              <Box
                component="button"
                type="button"
                aria-label={label}
                aria-pressed={view === key}
                onClick={() => selectView(key)}
                sx={{
                  display: "flex",
                  p: 1.25,
                  border: "1px solid",
                  borderColor: view === key ? "rgba(99, 102, 241, 0.35)" : "transparent",
                  backgroundColor: view === key ? "rgba(99, 102, 241, 0.08)" : "transparent",
                  color: view === key ? "#4f46e5" : "#94a3b8",
                  borderRadius: "10px",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "rgba(241, 245, 249, 0.9)" },
                }}
              >
                <Icon size={18} />
              </Box>
            </Tooltip>
          ))}
        </Box>
      </Box>

      {/* Empty states */}
      {!hasWorkspaces && (
        <Paper
          className="glass-panel"
          sx={{ borderRadius: "16px", p: 8, textAlign: "center", border: "none" }}
        >
          <Box sx={{ color: "#c7d2fe", display: "flex", justifyContent: "center", mb: 2 }}>
            <WorkspaceIcon size={48} />
          </Box>
          <Typography sx={{ fontWeight: 600, color: "#0f172a", mb: 0.5 }}>
            No workspaces yet
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            A workspace holds your content models, entries and media. Create one to start.
          </Typography>
          <AddButton label="Create workspace" onClick={() => setModalOpen(true)} />
        </Paper>
      )}

      {hasWorkspaces && !hasResults && (
        <Paper
          className="glass-panel"
          sx={{ borderRadius: "16px", p: 8, textAlign: "center", border: "none" }}
        >
          <Typography sx={{ fontWeight: 600, color: "#0f172a", mb: 0.5 }}>
            No workspaces match “{searchTerm}”
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Check the spelling, or search by the owner’s name.
          </Typography>
        </Paper>
      )}

      {/* Card view */}
      {hasResults && view === "grid" && (
        <Grid2 container spacing={2.5}>
          {visible.map((workspace) => {
            const accent = accentFor(workspace._id);
            const showLogo = Boolean(workspace.logoUrl) && !brokenLogos.has(workspace._id);
            return (
              <Grid2 key={workspace._id} size={{ xs: 12, sm: 6, lg: 4 }}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => open(workspace._id)}
                  className="glass-card"
                  sx={{
                    width: "100%",
                    height: "100%",
                    textAlign: "left",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    p: 2.5,
                    borderRadius: "16px",
                    border: "1px solid rgba(226, 232, 240, 0.9)",
                    cursor: "pointer",
                    "&:focus-visible": {
                      outline: "2px solid #6366f1",
                      outlineOffset: "2px",
                    },
                  }}
                >
                  {/* Identity */}
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        flexShrink: 0,
                        borderRadius: "12px",
                        overflow: "hidden",
                        backgroundColor: showLogo ? "#fff" : accent.bg,
                        border: showLogo ? "1px solid rgba(226, 232, 240, 0.9)" : "none",
                        color: accent.fg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: 15,
                        letterSpacing: "0.02em",
                      }}
                    >
                      {showLogo ? (
                        <Box
                          component="img"
                          src={workspace.logoUrl}
                          alt=""
                          loading="lazy"
                          // A logo that 404s falls back to the monogram rather
                          // than leaving an empty tile.
                          onError={() =>
                            setBrokenLogos((previous) => new Set(previous).add(workspace._id))
                          }
                          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        monogramFor(workspace.name)
                      )}
                    </Box>
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography
                        noWrap
                        sx={{ fontWeight: 600, fontSize: 16, color: "#0f172a", lineHeight: 1.3 }}
                      >
                        {workspace.name}
                      </Typography>
                      <Typography noWrap sx={{ fontSize: 12.5, color: "#94a3b8" }}>
                        {workspace.isOwnedByMe ? "Owned by you" : `Created by ${workspace.ownerName}`}
                      </Typography>
                    </Box>
                    {workspace.role && (
                      <Chip
                        label={workspace.role}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: 11,
                          fontWeight: 600,
                          textTransform: "capitalize",
                          flexShrink: 0,
                          ...(ROLE_STYLES[workspace.role] || ROLE_STYLES.viewer),
                        }}
                      />
                    )}
                  </Box>

                  {/* Size and shape of the workspace */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      justifyContent: "space-between",
                      py: 1.5,
                      borderTop: "1px solid rgba(226, 232, 240, 0.8)",
                      borderBottom: "1px solid rgba(226, 232, 240, 0.8)",
                    }}
                  >
                    <Stat
                      icon={<ContentIcon size={18} />}
                      value={workspace.entries === null ? "—" : workspace.entries.toLocaleString()}
                      label={workspace.entries === 1 ? "entry" : "entries"}
                    />
                    <Stat
                      icon={<UsersIcon size={18} />}
                      value={workspace.memberCount}
                      label={workspace.memberCount === 1 ? "member" : "members"}
                    />
                    <Stat
                      icon={<KeyIcon size={18} />}
                      value={workspace.apiKeys}
                      label={workspace.apiKeys === 1 ? "API key" : "API keys"}
                    />
                  </Box>

                  {/* Configuration */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                      mt: "auto",
                    }}
                  >
                    {workspace.locales.slice(0, 3).map((locale) => (
                      <Chip
                        key={locale}
                        label={locale}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: 11,
                          fontWeight: 500,
                          color: "#475569",
                          backgroundColor: "rgba(100, 116, 139, 0.10)",
                        }}
                      />
                    ))}
                    {workspace.locales.length > 3 && (
                      <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                        +{workspace.locales.length - 3}
                      </Typography>
                    )}
                    {workspace.aiModel && (
                      <Tooltip title={`Default AI model: ${workspace.aiModel}`} arrow>
                        <Chip
                          label={workspace.aiModel}
                          size="small"
                          sx={{
                            height: 22,
                            maxWidth: 150,
                            fontSize: 11,
                            fontWeight: 500,
                            color: "#4f46e5",
                            backgroundColor: "rgba(99, 102, 241, 0.10)",
                          }}
                        />
                      </Tooltip>
                    )}
                    {workspace.createdAt && (
                      <Typography sx={{ fontSize: 11, color: "#94a3b8", ml: "auto" }}>
                        {workspace.createdAt}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid2>
            );
          })}
        </Grid2>
      )}

      {/* Table view */}
      {hasResults && view === "list" && (
        <Paper
          className="glass-panel"
          sx={{
            borderRadius: "16px",
            overflow: "hidden",
            border: "none",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
          }}
        >
          <DataGrid
            rows={visible}
            columns={columns}
            disableColumnSelector
            disableRowSelectionOnClick
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            pageSizeOptions={[10, 25, 50]}
            onRowClick={(params) => open(params.row._id)}
            sx={{
              border: "none",
              "& .MuiDataGrid-cell:focus": { outline: "none" },
              "& .MuiDataGrid-row:hover": {
                cursor: "pointer",
                backgroundColor: "rgba(99, 102, 241, 0.04)",
              },
              minHeight: 400,
            }}
          />
        </Paper>
      )}

      <WorkspaceModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        refreshList={() => setShouldRefresh(true)}
      />

      {loading && <LoadingOverlay />}
    </Box>
  );
};

export default WorkspaceList;
